import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Layout } from "@/components/layout/Layout";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { isAwaitingFirstResult, useTenantOrgId } from "@/hooks/useTenantOrgId";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Phone, MapPin, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageSeo } from "@/components/seo/PageSeo";
import { useDemoShowcase } from "@/hooks/useDemoShowcase";
import {
  useContactCompanyInfo,
  useContactBusinessHours,
  useSubmitContactForm,
} from "@/hooks/useContactPage";
import {
  TENANT_ENQUIRY_TYPES,
  tenantContactSchema,
  type TenantContactValues,
} from "@/lib/enquiry-types";

const EMPTY_FORM = { name: "", email: "", phone: "", message: "" };

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ContactPage = () => {
  const orgId = useTenantOrgId();
  const demo = useDemoShowcase();
  const formSectionRef = useRef<HTMLElement | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const form = useForm<TenantContactValues>({
    resolver: zodResolver(tenantContactSchema),
    defaultValues: EMPTY_FORM,
  });

  // Non-data effect — scroll to the form section on mount. Preserved as-is.
  useEffect(() => {
    window.requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({ block: 'start' });
    });
  }, []);

  const companyPendingQuery = useContactCompanyInfo(orgId);
  const { data: companyInfo } = companyPendingQuery;
  const companyPending = isAwaitingFirstResult(companyPendingQuery);
  const hoursPendingQuery = useContactBusinessHours(orgId);
  const { data: businessHours = [] } = hoursPendingQuery;
  const hoursPending = isAwaitingFirstResult(hoursPendingQuery);
  const submitContactForm = useSubmitContactForm();

  const handleSubmit = async (values: TenantContactValues) => {
    try {
      await submitContactForm.mutateAsync({
        orgId: orgId!,
        name: values.name,
        email: values.email,
        phone: values.phone ?? "",
        enquiryType: values.enquiryType,
        message: values.message,
      });
      setSubmitted(true);
      form.reset(EMPTY_FORM);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    }
  };

  const formatTime = (time: string) => {
    const [hours] = time.split(':');
    const hour = parseInt(hours);
    if (hour === 0) return '12am';
    if (hour === 12) return '12pm';
    return hour > 12 ? `${hour - 12}pm` : `${hour}am`;
  };

  return (
    <Layout>
      <PageSeo pageSlug="contact" fallbackTitle="Contact" fallbackDescription="Contact the cleaning team." />
      {/* Hero */}
      <section className="relative -mt-20 overflow-hidden pb-10 pt-32 md:pb-14 md:pt-40">
        <div className="tp-hero-wash" aria-hidden />
        <div className="container relative mx-auto px-5 text-center md:px-6">
          <p className="tp-eyebrow tp-seq" style={{ ["--seq" as string]: 0 }}>Get in touch</p>
          <h1 className="tp-display tp-display--md tp-seq mt-6" style={{ ["--seq" as string]: 1 }}>
            Contact Us
          </h1>
          <p className="tp-lead tp-seq mx-auto mt-5 max-w-2xl" style={{ ["--seq" as string]: 2 }}>
            {demo?.contact.lead ?? "Get in touch with our team. We're here to help with all your cleaning needs."}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section ref={formSectionRef} className="scroll-mt-24 py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <Card className="tp-card">
              <CardHeader>
                <CardTitle className="text-2xl">Send us a message</CardTitle>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground mb-6">
                      Thank you for getting in touch. We'll respond within 24 hours.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6" noValidate>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" autoComplete="name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email *</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="your@email.com" autoComplete="email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone (optional)</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="Your phone number" autoComplete="tel" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="enquiryType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What's your enquiry about? *</FormLabel>
                          <Select value={field.value ?? ""} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger onBlur={field.onBlur}>
                                <SelectValue placeholder="Choose one" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {TENANT_ENQUIRY_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message *</FormLabel>
                          <FormControl>
                            <Textarea placeholder="How can we help you?" rows={5} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" className="w-full" disabled={submitContactForm.isPending}>
                      {submitContactForm.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                  </Form>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            {companyPending || hoursPending ? (
              <div className="tp-skeleton space-y-8" aria-busy="true">
                <LoadingLabel>Loading contact details</LoadingLabel>
                <Card aria-hidden>
                  <CardContent className="space-y-6 pt-6">
                    {[0, 1].map((index) => (
                      <div key={index} className="flex items-start gap-4">
                        <Skel className="h-12 w-12 shrink-0 rounded-lg" />
                        <div className="flex-1 space-y-2.5 pt-1">
                          <Skel className="h-4 w-24 tp-skel-round" />
                          <Skel className="h-4 w-44 tp-skel-round" />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card aria-hidden>
                  <CardContent className="space-y-4 pt-6">
                    <Skel className="h-5 w-36 tp-skel-round" />
                    {[0, 1, 2, 3, 4].map((index) => (
                      <div key={index} className="flex justify-between py-1">
                        <Skel className="h-3.5 w-20 tp-skel-round" />
                        <Skel className="h-3.5 w-24 tp-skel-round" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            ) : (
            <div className="tp-loaded space-y-8">
              {/* Quick Contact */}
              <Card>
                <CardContent className="pt-6 space-y-6">
                  {companyInfo?.phone_number && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Phone</h3>
                        <a
                          href={`tel:${companyInfo.phone_number}`}
                          className="text-primary hover:underline text-lg"
                        >
                          {companyInfo.phone_number}
                        </a>
                      </div>
                    </div>
                  )}

                  {companyInfo?.service_areas && companyInfo.service_areas.length > 0 && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Service Areas</h3>
                        <p className="text-muted-foreground">
                          {companyInfo.service_areas.join(', ')}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Business Hours */}
              {businessHours.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">Business Hours</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {businessHours.map((hour) => (
                        <div
                          key={hour.day_of_week}
                          className={cn(
                            "flex justify-between py-2 border-b border-border last:border-0",
                            !hour.is_open && "text-muted-foreground"
                          )}
                        >
                          <span className="font-medium">{DAYS[hour.day_of_week]}</span>
                          <span>
                            {hour.is_open
                              ? `${formatTime(hour.open_time)} - ${formatTime(hour.close_time)}`
                              : 'Closed'
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Service Area Map */}
              {companyInfo?.service_areas && companyInfo.service_areas.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Our Service Areas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                      <iframe
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(companyInfo.service_areas[0] + ', UK')}&zoom=11`}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {companyInfo.service_areas.map((area, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-md text-sm font-medium"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
